import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import { Loader2, EyeOff, Trash2, Globe } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

// Browse currently-LIVE blog posts and unpublish (reversible) or permanently delete one.
export default function PublishedPosts() {
  const [posts, setPosts] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/content/posts?status=published");
      setPosts(data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load published articles");
      setPosts([]);
    }
  };
  useEffect(() => { load(); }, []);

  const unpublish = async (p) => {
    setBusy(p.id);
    try {
      await api.put(`/admin/content/posts/${p.id}`, { status: "draft" });
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`Unpublished — "${p.title}" is off the public site (reversible in Chat calendar)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't unpublish");
    }
    setBusy("");
  };

  const remove = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"? This removes the article and its image for good — it can't be undone.`)) return;
    setBusy(p.id);
    try {
      await api.delete(`/admin/content/posts/${p.id}`);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Deleted permanently");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
    setBusy("");
  };

  if (posts === null) {
    return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" /></div>;
  }

  return (
    <div data-testid="published-posts" className="space-y-2">
      {posts.length === 0 && <p className="text-sm text-slate-500">No published articles yet.</p>}
      {posts.map((p) => (
        <div key={p.id} data-testid={`published-post-${p.id}`}
             className="flex items-center gap-3 rounded-xl border border-[#1E273D] bg-[#07090E] p-3">
          <Globe className="w-4 h-4 text-green-400 shrink-0" strokeWidth={1.5} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-200 truncate">{p.title}</div>
            <div className="text-xs text-slate-500">published {fmtDate(p.published_at)}</div>
          </div>
          <button data-testid={`unpublish-btn-${p.id}`} onClick={() => unpublish(p)} disabled={busy === p.id}
                  title="Pull off the public site — reversible, can republish later"
                  className="h-8 px-3 rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 shrink-0">
            {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />} Unpublish
          </button>
          <button data-testid={`delete-published-btn-${p.id}`} onClick={() => remove(p)} disabled={busy === p.id}
                  title="Permanently delete this article and its image"
                  className="h-8 px-3 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 shrink-0">
            {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
          </button>
        </div>
      ))}
    </div>
  );
}
